import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export const useAppAlertDialog = ({
  cancelLabel = "Avbryt",
  confirmLabel = "Fortsätt",
  closeLabel = "Stäng",
} = {}) => {
  const [state, setState] = useState({
    open: false,
    title: "",
    description: "",
    mode: "alert",
    onConfirm: undefined,
    onCancel: undefined,
  });

  const close = useCallback(() => {
    setState((prev) => ({
      ...prev,
      open: false,
      onConfirm: undefined,
      onCancel: undefined,
    }));
  }, []);

  const showAlert = useCallback((title, description) => {
    setState({
      open: true,
      title: String(title || "Något gick fel"),
      description: String(description || ""),
      mode: "alert",
      onConfirm: undefined,
      onCancel: undefined,
    });
  }, []);

  const showConfirm = useCallback((title, description) =>
    new Promise((resolve) => {
      setState({
        open: true,
        title: String(title || "Bekräfta"),
        description: String(description || ""),
        mode: "confirm",
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    }), []);

  const dialogNode = useMemo(
    () =>
      state.open ? (
        <AlertDialog open={state.open} onOpenChange={(open) => !open && close()}>
          <AlertDialogContent
            onEscapeKeyDown={(event) => {
              if (state.mode === "confirm") event.preventDefault();
            }}
            onPointerDownOutside={(event) => {
              if (state.mode === "confirm") event.preventDefault();
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{state.title}</AlertDialogTitle>
              <AlertDialogDescription>{state.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              {state.mode === "confirm" ? (
                <>
                  <AlertDialogCancel
                    onClick={() => {
                      state.onCancel?.();
                      close();
                    }}
                  >
                    {cancelLabel}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      state.onConfirm?.();
                      close();
                    }}
                  >
                    {confirmLabel}
                  </AlertDialogAction>
                </>
              ) : (
                <AlertDialogAction onClick={close}>{closeLabel}</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null,
    [state, cancelLabel, confirmLabel, closeLabel, close]
  );

  return { showAlert, showConfirm, dialogNode, close };
};
